import { useApi, useMessage } from 'app/context';
import { useBasePaginatedList } from './useBasePaginatedList';
import { IReportSchema, IReportStatus } from 'generated/axios';
import { useCallback, useEffect, useState } from 'react';
import { trimFileName } from 'app/utils';
import { useForm } from './useForm';
import { t } from 'i18next';

export const useReportList = () => {
  const { reportApi } = useApi();
  const { success } = useMessage();

  type Filters = {
    searchQuery?: string;
    patientId?: string;
    doctorId?: string;
    companyId?: string;
    reportStatus?: IReportStatus;
  };

  const {
    list,
    page,
    handlePageChange,
    pageSize,
    handlePageSizeChange,
    loading,
    setLoading,
    count,
    handleSuccess,
    handleError,
  } = useBasePaginatedList<IReportSchema>();

  const [additionalFilters, setAdditionalFilters] = useState<Filters>();

  const additionalFiltersForm = useForm<Filters>({
    onSubmit: setAdditionalFilters,
  });

  const get = useCallback(() => {
    setLoading(true);
    reportApi
      .webPaginateReports(
        page,
        pageSize,
        additionalFilters?.searchQuery,
        additionalFilters?.patientId,
        additionalFilters?.doctorId,
        additionalFilters?.companyId,
        additionalFilters?.reportStatus,
      )
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        handleSuccess(resp.data.results, resp.data.total);
      })
      .catch((err) => {
        handleError(err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportApi, page, pageSize, additionalFilters]);

  //GET PDF
  const getPdf = (reportId: string) => {
    reportApi
      .webGenerateReportPDF(
        reportId,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        { responseType: 'blob' })
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        const blob = new Blob([resp.data as unknown as BlobPart]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = trimFileName(resp.headers?.['content-disposition']) ?? 'download';
        a.click();

        window.URL.revokeObjectURL(url);
      })
      .catch(handleError);
  };

  const deleteByIds = useCallback(
    (reportIds: string[]) => {
      setLoading(true);
      reportApi
        .webBulkDeleteReports({ report_ids: reportIds })
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          resp.data && success({ message: t('report.bulkDeleteSuccess') as string });
          get();
        })
        .catch(handleError)
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportApi],
  );

  useEffect(() => {
    get();
  }, [get]);

  return {
    list,
    loading,
    count,
    page,
    handlePageChange,
    pageSize,
    handlePageSizeChange,
    getPdf,
    additionalFiltersForm,
    deleteByIds,
  };
};
